// Store audio context globally to reuse and resume if suspended
let globalAudioContext: AudioContext | null = null;
let audioInitialized = false;

/**
 * Gets or creates an audio context, resuming if suspended
 * Modern browsers require user interaction before allowing audio
 */
export const getAudioContext = async (): Promise<AudioContext | null> => {
  try {
    if (!globalAudioContext) {
      globalAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log("🔊 Audio context created, state:", globalAudioContext.state);
    }

    // Resume audio context if it's suspended (required after user interaction)
    if (globalAudioContext.state === "suspended") {
      console.log("🔊 Attempting to resume audio context...");
      await globalAudioContext.resume();
      console.log("🔊 Audio context resumed, new state:", globalAudioContext.state);
    }

    audioInitialized = true;
    return globalAudioContext;
  } catch (error) {
    console.error("❌ Failed to create/resume audio context:", error);
    return null;
  }
};

/**
 * Pre-initializes audio context (call this on user interaction)
 */
export const initializeAudio = async () => {
  await getAudioContext();
};

/**
 * Plays a ringing notification sound for SOS alerts
 * Uses Web Audio API to generate a phone-like ringing sound pattern
 */
export const playSosRingingSound = async () => {
  try {
    console.log("🔊 Attempting to play SOS ringing sound...");
    console.log("🔊 Audio initialized:", audioInitialized);

    const audioContext = await getAudioContext();
    if (!audioContext) {
      console.warn("⚠️ Audio context not available - may need user interaction");
      // Try one more time with a new context
      try {
        const newContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        if (newContext.state === "suspended") {
          await newContext.resume();
        }
        if (newContext.state === "running") {
          globalAudioContext = newContext;
          console.log("🔊 Created new audio context successfully");
        } else {
          console.warn("⚠️ Audio context still suspended - browser autoplay policy may be blocking");
          return;
        }
      } catch (e) {
        console.error("❌ Failed to create new audio context:", e);
        return;
      }
    }

    if (!globalAudioContext) {
      console.error("❌ No audio context available");
      return;
    }

    const finalContext = globalAudioContext;
    console.log("🔊 Audio context state:", finalContext.state);

    // Resume if suspended
    if (finalContext.state === "suspended") {
      console.warn("⚠️ Audio context suspended, attempting to resume...");
      await finalContext.resume();
      console.log("🔊 Audio context state after resume:", finalContext.state);
    }

    // Check if we can proceed
    if (finalContext.state !== "running") {
      console.error("❌ Cannot play sound - audio context is", finalContext.state, ". User interaction may be required.");
      return;
    }

    // Play 3 rings with alternating tones (like a phone)
    const playRing = (startTime: number, frequency1: number, frequency2: number) => {
      const duration = 0.2; // 200ms per tone

      // First tone
      const oscillator1 = finalContext.createOscillator();
      const gainNode1 = finalContext.createGain();
      oscillator1.connect(gainNode1);
      gainNode1.connect(finalContext.destination);

      oscillator1.frequency.value = frequency1;
      oscillator1.type = "sine";
      gainNode1.gain.setValueAtTime(0.5, finalContext.currentTime);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, finalContext.currentTime + duration);

      oscillator1.start(startTime);
      oscillator1.stop(startTime + duration);

      // Second tone (slightly after first)
      const oscillator2 = finalContext.createOscillator();
      const gainNode2 = finalContext.createGain();
      oscillator2.connect(gainNode2);
      gainNode2.connect(finalContext.destination);

      oscillator2.frequency.value = frequency2;
      oscillator2.type = "sine";
      gainNode2.gain.setValueAtTime(0.5, finalContext.currentTime);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, finalContext.currentTime + duration);

      oscillator2.start(startTime + duration);
      oscillator2.stop(startTime + duration * 2);
    };

    const currentTime = finalContext.currentTime;
    const ringInterval = 0.6; // 600ms between rings

    // Play 3 rings
    playRing(currentTime, 440, 554); // Ring 1: A4 and C#5
    playRing(currentTime + ringInterval, 440, 554); // Ring 2
    playRing(currentTime + ringInterval * 2, 440, 554); // Ring 3

    console.log("✅ SOS ringing sound started");
  } catch (error) {
    console.error("❌ Failed to play SOS ringing sound:", error);
  }
};

